import { Component, ElementRef, ViewChild, signal, inject, HostListener, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@app/core/services/auth.service';
import { UserService } from '@app/core/services/user.service';
import { CartService } from '@app/core/services/cart.service';
import { PackageService } from '@app/core/services/package.service';
import { PackageListItem, PackageProduct } from '@app/core/models/package.model';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface SearchResultPackage {
  type: 'package';
  id: string;
  name: string;
  manufacturer: string;
  imageUrl?: string;
  anchor: string;
}

interface SearchResultProduct {
  type: 'product';
  id: string;
  name: string;
  packageName: string;
  imageUrl?: string;
  anchor: string;
}

type SearchResult = SearchResultPackage | SearchResultProduct;

interface Language {
  code: string;
  name: string;
  flag: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, TranslateModule, CommonModule, FormsModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mobileSearchInput') mobileSearchInput!: ElementRef<HTMLInputElement>;

  searchActive = signal(false);
  mobileSearchActive = signal(false);
  searchQuery = signal('');
  searchResults = signal<SearchResult[]>([]);
  searchLoading = signal(false);
  searchDataLoaded = signal(false);
  private allPackages: PackageListItem[] = [];
  private allProducts: PackageProduct[] = [];
  private searchDebounce: any = null;

  userMenuOpen = signal(false);
  languageMenuOpen = signal(false);
  currentLang: string;
  private userService = inject(UserService);
  private packageService = inject(PackageService);
  private router = inject(Router);
  cartService = inject(CartService);

  languages: Language[] = [
    { code: 'de', name: 'Deutsch', flag: 'de' },
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'es', name: 'Español', flag: 'es' },
    { code: 'it', name: 'Italiano', flag: 'it' },
    { code: 'fr', name: 'Français', flag: 'fr' },
    { code: 'nl', name: 'Nederlands', flag: 'nl' }
  ];

  constructor(
    public authService: AuthService,
    private translate: TranslateService
  ) {
    this.currentLang = this.translate.currentLang || this.translate.defaultLang || 'de';
    this.translate.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
    });
  }

  ngOnInit(): void {
    // Load cart when user is authenticated
    if (this.authService.isAuthenticated()) {
      this.cartService.loadCart();
    }
  }

  toggleSearch(): void {
    this.searchActive.update(v => !v);
    if (this.searchActive()) {
      setTimeout(() => this.searchInput?.nativeElement.focus(), 100);
      this.ensureSearchDataLoaded();
    } else {
      this.clearSearch();
    }
  }

  toggleMobileSearch(): void {
    this.mobileSearchActive.update(v => !v);
    if (this.mobileSearchActive()) {
      setTimeout(() => this.mobileSearchInput?.nativeElement.focus(), 100);
      this.ensureSearchDataLoaded();
    } else {
      this.clearSearch();
    }
  }

  private ensureSearchDataLoaded(): void {
    if (this.searchDataLoaded() || this.searchLoading()) return;
    this.searchLoading.set(true);
    // Load all published packages + all products once
    this.packageService.search({ pageSize: 200 }).subscribe({
      next: (res) => {
        this.allPackages = (res.items || []).filter(p => p.status === 'published');
        this.packageService.getProducts().subscribe({
          next: (products) => {
            this.allProducts = products || [];
            this.searchDataLoaded.set(true);
            this.searchLoading.set(false);
            // re-run filter if user already typed
            if (this.searchQuery()) this.runSearch(this.searchQuery());
          },
          error: () => {
            this.searchDataLoaded.set(true);
            this.searchLoading.set(false);
          }
        });
      },
      error: () => {
        this.searchLoading.set(false);
      }
    });
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.runSearch(value), 150);
  }

  private runSearch(query: string): void {
    const q = query.trim().toLowerCase();
    if (!q) {
      this.searchResults.set([]);
      return;
    }
    if (!this.searchDataLoaded()) return; // will re-run once loaded

    const pkgMatches: SearchResultPackage[] = this.allPackages
      .filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.manufacturer || '').toLowerCase().includes(q) ||
        (p.tags || '').toLowerCase().includes(q)
      )
      .slice(0, 5)
      .map(p => ({
        type: 'package' as const,
        id: p.id,
        name: p.name,
        manufacturer: p.manufacturer,
        imageUrl: p.imageUrl,
        anchor: this.manufacturerAnchor(p)
      }));

    const pkgById = new Map(this.allPackages.map(p => [p.id, p]));
    const productMatches: SearchResultProduct[] = this.allProducts
      .filter(pr =>
        pr.name.toLowerCase().includes(q) ||
        (pr.sku || '').toLowerCase().includes(q) ||
        (pr.packageName || '').toLowerCase().includes(q)
      )
      .slice(0, 8)
      .map(pr => {
        const owner = pkgById.get(pr.packageId);
        return {
          type: 'product' as const,
          id: pr.id,
          name: pr.name,
          packageName: pr.packageName,
          imageUrl: pr.imageUrl,
          anchor: owner ? this.manufacturerAnchor(owner) : ''
        };
      });

    this.searchResults.set([...pkgMatches, ...productMatches]);
  }

  private manufacturerAnchor(p: PackageListItem): string {
    return (p.manufacturer || p.name.split(' ')[0] || '').toUpperCase();
  }

  selectSearchResult(r: SearchResult): void {
    this.searchActive.set(false);
    this.mobileSearchActive.set(false);
    this.clearSearch();
    const anchor = r.anchor;
    if (!anchor) {
      // fallback: package detail
      if (r.type === 'package') this.router.navigate(['/packages', r.id]);
      return;
    }
    this.router.navigate(['/']).then(() => {
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    });
  }

  private clearSearch(): void {
    this.searchQuery.set('');
    this.searchResults.set([]);
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = null;
    }
  }

  toggleLanguageMenu(): void {
    this.languageMenuOpen.update(v => !v);
    if (this.languageMenuOpen()) {
      this.userMenuOpen.set(false);
    }
  }

  selectLanguage(langCode: string): void {
    this.translate.use(langCode);
    localStorage.setItem('wwhxp-language', langCode);
    this.languageMenuOpen.set(false);
    
    // Save to user profile if authenticated
    if (this.authService.isAuthenticated()) {
      this.userService.updateProfile({ preferredLanguage: langCode }).subscribe();
    }
  }

  getCurrentFlag(): string {
    const lang = this.languages.find(l => l.code === this.currentLang);
    return lang?.flag || 'de';
  }

  toggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
    if (this.userMenuOpen()) {
      this.languageMenuOpen.set(false);
    }
  }

  closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu') && !target.closest('.language-menu')) {
      this.userMenuOpen.set(false);
      this.languageMenuOpen.set(false);
    }
    if (!target.closest('.search-box') && !target.closest('.search-trigger') && !target.closest('.search-results-dropdown')) {
      if (this.searchActive()) {
        this.searchActive.set(false);
        this.clearSearch();
      }
      if (this.mobileSearchActive()) {
        this.mobileSearchActive.set(false);
        this.clearSearch();
      }
    }
  }

  logout(): void {
    this.closeUserMenu();
    this.authService.logout();
  }
}
